import type {
  SearchSnapshotFileNames,
  SearchSnapshotMails,
  SearchSnapshotPage,
} from '@affine/nbstore';
import {
  permissionSnapshotPartitionIndex,
  SEARCH_PARTITION_MAX_REQUESTED_DOCS,
  SEARCH_PARTITION_MAX_REQUESTED_MAILS,
  type SearchPartitionCatalogRow,
  type SearchPartitionContentRow,
  type SearchPartitionDigests,
  type SearchPartitionMailContentRow,
  type SearchPartitionMailRow,
  type SearchPartitionPage,
} from '@streamlain/permission-snapshot-contract';

/** Last server digest applied per partition index. */
export type AppliedSearchPartitionDigests = Readonly<Record<string, string>>;

/** One mail as the local index holds it, or as a search event names it. */
export interface SearchMailVersion {
  /** The mailbox page whose right is the right to read the mail. */
  docId: string;
  messageId: string;
  /** Hash of the indexed mail text; null when the mail left the index. */
  contentHash: string | null;
}

export interface SearchPartitionReconcilerDeps {
  fetchDigests(): Promise<SearchPartitionDigests>;
  fetchPage(
    partitionIndex: number,
    docIds: readonly string[],
    cursor: string | null,
    messageIds?: readonly string[],
    /**
     * Set for a refresh: the server answers only the requested pages and
     * mails, with catalog rows of these and the checked pages, instead of
     * walking the whole partition. A server without refresh walks it anyway.
     */
    checkedDocIds?: readonly string[]
  ): Promise<SearchPartitionPage>;
  /** Warm cache pages of this device, grouped by search partition. */
  warmCacheDocIdsByPartition(): ReadonlyMap<number, readonly string[]>;
  writePages(pages: readonly SearchSnapshotPage[]): void;
  /** Requested pages the server no longer lists as readable. */
  removePages(docIds: readonly string[]): void;
  /** Mails in the local index. */
  readLocalMails(): Promise<readonly SearchMailVersion[]>;
  writeMails(changes: readonly SearchSnapshotMails[]): void;
  /** File names of every listed page, so pages are found by files offline. */
  writeFileNames(changes: readonly SearchSnapshotFileNames[]): void;
  readApplied(): AppliedSearchPartitionDigests;
  writeApplied(applied: AppliedSearchPartitionDigests): void;
}

interface ReplaceOptions {
  /** Pages whose access is checked against the catalog, without content. */
  checked?: readonly string[];
  /** Also make the partition's mails equal to the server's. */
  mails?: boolean;
  /** Ask only for the given pages, not for the whole partition's catalog. */
  refresh?: boolean;
}

/**
 * Compares the server's partition digests with the digests applied last
 * time and replaces only the partitions that differ: their warm cache pages
 * get the server's search content and their mails become the server's,
 * fetching the text of new and changed mails only. A digest is stored only
 * after its partition was written, so a failed run leaves the stored state
 * and the local index as they were and the next run repeats the missing
 * partitions.
 */
export class SearchPartitionReconciler {
  constructor(private readonly deps: SearchPartitionReconcilerDeps) {}

  /** Returns the indexes of the replaced partitions. */
  async reconcile(): Promise<number[]> {
    const { partitions } = await this.deps.fetchDigests();
    const stored: Record<string, string> = { ...this.deps.readApplied() };
    const replaced: number[] = [];
    let warmCacheByPartition: ReadonlyMap<number, readonly string[]> | null =
      null;
    let localMailPartitions: ReadonlySet<number> | null = null;

    for (const { partitionIndex, digest, mailCount } of partitions) {
      const key = String(partitionIndex);
      if (stored[key] === digest) continue;
      warmCacheByPartition ??= this.deps.warmCacheDocIdsByPartition();
      localMailPartitions ??= new Set(
        (await this.deps.readLocalMails()).map(mail =>
          permissionSnapshotPartitionIndex('doc', mail.docId)
        )
      );
      await this.replace(
        partitionIndex,
        warmCacheByPartition.get(partitionIndex) ?? [],
        {
          mails: mailCount > 0 || localMailPartitions.has(partitionIndex),
        }
      );
      stored[key] = digest;
      this.deps.writeApplied({ ...stored });
      replaced.push(partitionIndex);
    }

    // A partition the server no longer lists holds no readable page.
    const listed = new Set(partitions.map(p => String(p.partitionIndex)));
    const kept = Object.fromEntries(
      Object.entries(stored).filter(([key]) => listed.has(key))
    );
    if (Object.keys(kept).length !== Object.keys(stored).length) {
      this.deps.writeApplied(kept);
    }
    return replaced;
  }

  /**
   * Fetches the server's content of single pages after a search or
   * permission event; a page missing from its partition catalog is no
   * longer readable and leaves the index. `checkedDocIds` are only checked
   * for access: they leave the index with their mails when unreadable.
   */
  async refreshDocs(
    docIds: readonly string[],
    checkedDocIds: readonly string[] = []
  ): Promise<void> {
    const requested = byPartition(docIds);
    const checked = byPartition(checkedDocIds);
    for (const partitionIndex of new Set([
      ...requested.keys(),
      ...checked.keys(),
    ])) {
      await this.replace(partitionIndex, requested.get(partitionIndex) ?? [], {
        checked: checked.get(partitionIndex),
        refresh: true,
      });
    }
  }

  /** Applies changed mails of search events: new text in, removed mails out. */
  async refreshMails(mails: readonly SearchMailVersion[]): Promise<void> {
    const changes = new MailChanges();
    const wanted = new Map<number, string[]>();
    for (const mail of mails) {
      if (mail.contentHash === null) {
        changes.remove(mail.docId, mail.messageId);
        continue;
      }
      const partition = permissionSnapshotPartitionIndex('doc', mail.docId);
      const ids = wanted.get(partition);
      if (ids) ids.push(mail.messageId);
      else wanted.set(partition, [mail.messageId]);
    }
    for (const [partitionIndex, messageIds] of wanted) {
      await this.fetchMails(partitionIndex, messageIds, changes);
    }
    changes.write(this.deps);
  }

  private async replace(
    partitionIndex: number,
    docIds: readonly string[],
    { checked = [], mails = false, refresh = false }: ReplaceOptions = {}
  ): Promise<void> {
    const docChunks = requestChunks(docIds);
    // A refresh sends the checked pages along; a full walk lists every
    // readable page and mail, also without content.
    const checkedChunks = refresh ? requestChunks(checked) : [];
    const walks: { chunk: readonly string[]; checked: readonly string[] }[] =
      [];
    for (
      let n = 0;
      n < Math.max(docChunks.length, checkedChunks.length);
      n += 1
    ) {
      walks.push({
        chunk: docChunks[n] ?? [],
        checked: checkedChunks[n] ?? [],
      });
    }
    // A full walk always lists the catalog: its file names are kept for
    // every readable page, also without content.
    if (!refresh && walks.length === 0) walks.push({ chunk: [], checked: [] });

    let listedMails: SearchPartitionMailRow[] | null = null;
    for (const walk of walks) {
      const { chunk } = walk;
      const catalog = new Map<string, SearchPartitionCatalogRow>();
      const rows = new Map<string, SearchPartitionContentRow[]>();
      const listed: SearchPartitionMailRow[] = [];
      const fileNames = new Map<string, string[]>();
      let cursor: string | null = null;
      do {
        const page = await this.deps.fetchPage(
          partitionIndex,
          chunk,
          cursor,
          [],
          refresh ? walk.checked : undefined
        );
        for (const row of page.catalog) catalog.set(row.docId, row);
        for (const row of page.content) {
          const docRows = rows.get(row.docId);
          if (docRows) docRows.push(row);
          else rows.set(row.docId, [row]);
        }
        listed.push(...page.mails);
        for (const { docId, name } of page.fileNames) {
          const names = fileNames.get(docId);
          if (names) names.push(name);
          else fileNames.set(docId, [name]);
        }
        cursor = page.nextCursor;
      } while (cursor !== null);

      const pages: SearchSnapshotPage[] = [];
      const unreadable: string[] = [];
      for (const docId of chunk) {
        const entry = catalog.get(docId);
        if (!entry) {
          unreadable.push(docId);
          continue;
        }
        // Not yet indexed on the server: the crawler keeps the page.
        if (entry.contentVersion === null && entry.fileTextVersion === null) {
          continue;
        }
        pages.push({
          docId,
          contentVersion: entry.contentVersion,
          fileTextVersion: entry.fileTextVersion,
          rows: rows.get(docId) ?? [],
        });
      }
      // A refresh catalog lists the pages of its walk; a full walk's catalog
      // lists all readable pages, so they are checked once.
      const checkNow = refresh ? walk.checked : listedMails ? [] : checked;
      for (const docId of checkNow)
        if (!catalog.has(docId)) unreadable.push(docId);
      if (!refresh && listedMails === null) {
        // Every listed page gets its names, an empty list clears old ones.
        this.deps.writeFileNames(
          [...catalog.keys()].map(docId => ({
            docId,
            names: fileNames.get(docId) ?? [],
          }))
        );
      }
      listedMails ??= listed;
      if (pages.length > 0) this.deps.writePages(pages);
      if (unreadable.length > 0) this.deps.removePages(unreadable);
    }
    if (mails && listedMails)
      await this.replaceMails(partitionIndex, listedMails);
  }

  /** Makes the partition's local mails equal to the listed server mails. */
  private async replaceMails(
    partitionIndex: number,
    listed: readonly SearchPartitionMailRow[]
  ) {
    const local = new Map(
      (await this.deps.readLocalMails())
        .filter(
          mail =>
            permissionSnapshotPartitionIndex('doc', mail.docId) ===
            partitionIndex
        )
        .map(mail => [mail.messageId, mail])
    );
    const server = new Map(listed.map(mail => [mail.messageId, mail]));
    const changes = new MailChanges();
    for (const mail of local.values()) {
      if (server.get(mail.messageId)?.mailboxDocId !== mail.docId)
        changes.remove(mail.docId, mail.messageId);
    }
    const wanted = listed
      .filter(mail => {
        const own = local.get(mail.messageId);
        return (
          own?.docId !== mail.mailboxDocId ||
          own.contentHash !== mail.contentHash
        );
      })
      .map(mail => mail.messageId);
    await this.fetchMails(partitionIndex, wanted, changes);
    changes.write(this.deps);
  }

  /** Text of the given mails, at most the request limit per walk. */
  private async fetchMails(
    partitionIndex: number,
    messageIds: readonly string[],
    changes: MailChanges
  ) {
    for (
      let start = 0;
      start < messageIds.length;
      start += SEARCH_PARTITION_MAX_REQUESTED_MAILS
    ) {
      const chunk = messageIds.slice(
        start,
        start + SEARCH_PARTITION_MAX_REQUESTED_MAILS
      );
      let cursor: string | null = null;
      do {
        const page = await this.deps.fetchPage(
          partitionIndex,
          [],
          cursor,
          chunk,
          []
        );
        for (const mail of page.mailContent) changes.upsert(mail);
        cursor = page.nextCursor;
      } while (cursor !== null);
    }
  }
}

/** Mail changes grouped by mailbox page. */
class MailChanges {
  private readonly byDoc = new Map<
    string,
    { upsert: SearchPartitionMailContentRow[]; remove: string[] }
  >();

  upsert(mail: SearchPartitionMailContentRow) {
    this.of(mail.mailboxDocId).upsert.push(mail);
  }

  remove(docId: string, messageId: string) {
    this.of(docId).remove.push(messageId);
  }

  write(deps: Pick<SearchPartitionReconcilerDeps, 'writeMails'>) {
    if (this.byDoc.size === 0) return;
    deps.writeMails(
      [...this.byDoc].map(([docId, change]) => ({ docId, ...change }))
    );
  }

  private of(docId: string) {
    let change = this.byDoc.get(docId);
    if (!change) {
      change = { upsert: [], remove: [] };
      this.byDoc.set(docId, change);
    }
    return change;
  }
}

/** Splits ids into lists of at most the per-request page limit. */
function requestChunks(ids: readonly string[]): (readonly string[])[] {
  const chunks: (readonly string[])[] = [];
  for (
    let start = 0;
    start < ids.length;
    start += SEARCH_PARTITION_MAX_REQUESTED_DOCS
  ) {
    chunks.push(ids.slice(start, start + SEARCH_PARTITION_MAX_REQUESTED_DOCS));
  }
  return chunks;
}

function byPartition(docIds: readonly string[]) {
  const partitions = new Map<number, string[]>();
  for (const docId of new Set(docIds)) {
    const partition = permissionSnapshotPartitionIndex('doc', docId);
    const partitionDocIds = partitions.get(partition);
    if (partitionDocIds) partitionDocIds.push(docId);
    else partitions.set(partition, [docId]);
  }
  return partitions;
}

export interface SearchPartitionReconcileTiming {
  /** Time of the last successful run, null before the first one. */
  lastReconciledAt: number | null;
  /** Indexer has no queue and the device is not saving battery. */
  idle: boolean;
}

/** Due after one hour when the device is idle; the safety net after six. */
export const SEARCH_PARTITION_RECONCILE_DUE_MS = 60 * 60_000;
export const SEARCH_PARTITION_RECONCILE_MAX_MS = 6 * 60 * 60_000;

/** When the next reconcile is due; 0 before the first run. */
export function nextSearchPartitionReconcileAt({
  lastReconciledAt,
  idle,
}: SearchPartitionReconcileTiming): number {
  if (lastReconciledAt === null) return 0;
  return (
    lastReconciledAt +
    (idle
      ? SEARCH_PARTITION_RECONCILE_DUE_MS
      : SEARCH_PARTITION_RECONCILE_MAX_MS)
  );
}
